import { Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { JsonLd, usePageSEO } from '../../components/seo/JsonLd';

export default function Home() {
  usePageSEO(
    '株式会社SUNFIELD公式サイト｜京都市伏見区の運送会社',
    '株式会社SUNFIELDは関西圏を中心とした一般貨物運送事業・軽貨物運送事業を展開。安全・迅速・確実な輸送で、お客様から信頼される物流パートナーを目指します。',
    '運送,一般貨物,軽貨物,京都,関西,SUNFIELD,物流,配送,トラック'
  );

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "株式会社SUNFIELD",
    "alternateName": "SUNFIELD Transport Co., Ltd.",
    "url": `${import.meta.env.VITE_SITE_URL}/`,
    "logo": "https://static.readdy.ai/image/78304d8d02dcd7906feadaea6da56541/6d0a54398d10a3d029b8ef291a5e8385.png",
    "description": "関西圏を中心とした一般貨物運送事業・軽貨物運送事業を展開する物流会社",
    "foundingDate": "2023-09-01",
    "founder": {
      "@type": "Person",
      "name": "寸田翔馬"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "竹田藁屋町45 SOUBI BLD 2A",
      "addressLocality": "京都市伏見区",
      "addressRegion": "京都府",
      "postalCode": "612-8411",
      "addressCountry": "JP"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+81-90-9801-4659",
      "contactType": "customer service",
      "email": "sunfield.53@gmail.com",
      "hoursAvailable": "Mo-Fr 08:00-17:00"
    },
    "sameAs": [
      "https://www.instagram.com/sunfield.transport"
    ],
    "serviceArea": {
      "@type": "Place",
      "name": "関西圏（京都・大阪・奈良・兵庫）"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "運送サービス",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "一般貨物運送事業",
            "description": "3t・4tトラックによる関西圏での確実な配送サービス"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "軽貨物運送事業",
            "description": "小回りの利く軽貨物車両での迅速な配送サービス"
          }
        }
      ]
    }
  };

  const services = [
    {
      icon: 'ri-truck-line',
      title: '一般貨物運送事業',
      description: '3t・4tトラックによる関西圏での確実な貨物輸送サービス',
      features: ['大型貨物対応', '長距離輸送', '定期便運行']
    },
    {
      icon: 'ri-car-line',
      title: '軽貨物運送事業',
      description: '小回りの利く軽貨物車両での迅速な配送サービス',
      features: ['即日配送', '小口配送', 'EC物流対応']
    },
    {
      icon: 'ri-shield-check-line',
      title: '安全管理体制',
      description: '徹底した安全管理と品質保証で信頼をお届け',
      features: ['安全教育', '品質管理', '保険完備']
    }
  ];

  const achievements = [
    { number: '500+', label: '年間配送件数', unit: '件' },
    { number: '99.8', label: '配送成功率', unit: '%' },
    { number: '4', label: '対応エリア', unit: '府県' },
    { number: '24', label: '平均配送時間', unit: '時間以内' }
  ];

  const testimonials = [
    {
      name: '田中製造株式会社',
      role: '物流担当者',
      content: '迅速で丁寧な対応に大変満足しています。特に緊急配送の際の機動力は素晴らしく、信頼できるパートナーです。',
      rating: 5
    },
    {
      name: '関西EC販売',
      role: '代表取締役',
      content: 'EC事業の成長に合わせて柔軟に対応していただき、お客様からの評価も上がりました。',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={organizationSchema} />
      <Header />
      
      {/* ヒーローセクション */}
      <section 
        className="relative h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.7), rgba(30, 58, 138, 0.7)), url('https://public.readdy.ai/ai/img_res/537e66c69554b663bf2b520160296c8a.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            安全・迅速・確実な輸送で、<br />
            <span className="text-yellow-400">信頼と未来を運ぶ</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto">
            若手の力で物流を革新する、株式会社SUNFIELD<br />
            関西圏の物流を支える信頼のパートナー
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-blue-900 px-8 py-4 rounded-full font-bold text-lg hover:from-yellow-300 hover:to-yellow-400 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              お問い合わせ
            </Link>
            <Link
              to="/recruit"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-blue-900 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-user-add-line mr-2"></i>
              採用情報
            </Link>
          </div>
        </div>
        
        {/* スクロールインジケーター */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <i className="ri-arrow-down-line text-2xl"></i>
        </div>
      </section>

      {/* サービス概要 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              私たちのサービス
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              一般貨物から軽貨物まで、お客様のニーズに合わせた最適な物流ソリューションを提供します。
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-800 rounded-full flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-2xl text-yellow-400`}></i>
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-700">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-8 py-3 rounded-full font-medium hover:from-blue-800 hover:to-blue-700 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              サービス詳細を見る
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* お客様の声 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              お客様の声
            </h2>
            <p className="text-xl text-gray-600">
              実際にご利用いただいたお客様からの評価
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-building-line text-white"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-blue-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/voice"
              className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-8 py-3 rounded-full font-medium hover:from-blue-800 hover:to-blue-700 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              お客様の声をもっと見る
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* 採用セクション */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                一緒に働く仲間を募集中
              </h2>
              <p className="text-xl text-blue-200 mb-8">
                若手の力で物流業界を革新しませんか？<br />
                充実した待遇と成長できる環境をご用意しています。
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center text-blue-200">
                  <i className="ri-check-line text-yellow-400 mr-3"></i>
                  <span>3t・4tドライバー募集（月給28〜35万円）</span>
                </div>
                <div className="flex items-center text-blue-200">
                  <i className="ri-check-line text-yellow-400 mr-3"></i>
                  <span>各種保険完備・免許取得支援あり</span>
                </div>
                <div className="flex items-center text-blue-200">
                  <i className="ri-check-line text-yellow-400 mr-3"></i>
                  <span>勤務地：京都府八幡戸津東代64-1</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/recruit"
                  className="bg-yellow-400 text-blue-900 px-8 py-3 rounded-full font-bold hover:bg-yellow-300 transition-colors duration-200 text-center whitespace-nowrap cursor-pointer"
                >
                  採用情報を見る
                </Link>
                <Link
                  to="/partner"
                  className="border-2 border-white text-white px-8 py-3 rounded-full font-bold hover:bg-white hover:text-blue-900 transition-all duration-200 text-center whitespace-nowrap cursor-pointer"
                >
                  協力会社募集
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://static.readdy.ai/image/78304d8d02dcd7906feadaea6da56541/3856c98210813fe692b0a9b63551e336.jpeg"
                alt="採用情報"
                className="rounded-xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 画像セクション */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src="https://storage.readdy-site.link/project_files/6d5dba1b-33f7-4aa7-b92c-5272e7a9b8b9/da04d75c-0813-4ae3-b051-8be0c192f50e_-2026-04-05-0.44.18.png?v=818979ed44013972f95f06dc82774654"
            alt="SUNFIELD会社案内"
            className="w-full h-auto rounded-xl object-top"
          />
        </div>
      </section>

      {/* CTAセクション */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6">
            お気軽にお問い合わせください
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            物流に関するご相談、お見積もり、採用に関するお問い合わせなど、<br />
            どんなことでもお気軽にご連絡ください。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-8 py-4 rounded-full font-bold text-lg hover:from-blue-800 hover:to-blue-700 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              お問い合わせフォーム
            </Link>
            <a
              href="mailto:sunfield.53@gmail.com"
              className="border-2 border-blue-900 text-blue-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-blue-900 hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              メールで相談
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
